import { motion } from 'framer-motion';
import {
  Users,
  Shield,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  Target,
  BarChart3,
  Zap,
  Award,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

const themeClasses: Record<
  string,
  { badge: string; text: string; icon: string; soft: string; tint: string; tip: string }
> = {
  blue: {
    badge: 'bg-blue-600',
    text: 'text-blue-700',
    icon: 'text-blue-600',
    soft: 'bg-blue-100',
    tint: 'bg-blue-50',
    tip: 'bg-blue-50 border-blue-500',
  },
  purple: {
    badge: 'bg-purple-600',
    text: 'text-purple-700',
    icon: 'text-purple-600',
    soft: 'bg-purple-100',
    tint: 'bg-purple-50',
    tip: 'bg-purple-50 border-purple-500',
  },
  green: {
    badge: 'bg-green-600',
    text: 'text-green-700',
    icon: 'text-green-600',
    soft: 'bg-green-100',
    tint: 'bg-green-50',
    tip: 'bg-green-50 border-green-500',
  },
  red: {
    badge: 'bg-red-600',
    text: 'text-red-700',
    icon: 'text-red-600',
    soft: 'bg-red-100',
    tint: 'bg-red-50',
    tip: 'bg-red-50 border-red-500',
  },
  orange: {
    badge: 'bg-orange-600',
    text: 'text-orange-700',
    icon: 'text-orange-600',
    soft: 'bg-orange-100',
    tint: 'bg-orange-50',
    tip: 'bg-orange-50 border-orange-500',
  },
};

export default function AccountabilityOwnershipPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const raciRoles = [
    {
      letter: 'R',
      title: 'Responsible',
      color: 'blue',
      subtitle: 'Does the Work',
      description:
        "The person(s) who actually execute the work. They're in the trenches doing the tasks. There can be multiple Responsible parties.",
      responsibilities: [
        'Execute the work and complete tasks',
        'Update Accountable party on progress',
        'Escalate blockers and issues',
        'Deliver on commitments',
        'Collaborate with other Responsible parties',
      ],
      example:
        'For "Launch new product feature," Responsible = Product Manager + Engineers who build it + Designer who designs it',
      questions: [
        'Who will actually do this work?',
        'Do they have capacity?',
        'Do they have skills required?',
      ],
      tip: 'Can have multiple Rs. But if everyone is R, no one is R. Limit to those truly doing work, not just "involved."',
    },
    {
      letter: 'A',
      title: 'Accountable',
      color: 'purple',
      subtitle: 'Owns the Outcome',
      description:
        'The person who owns the outcome, makes the final decisions and ensures the work has the support it needs. Assign one accountable owner for each task.',
      responsibilities: [
        'Ensure work gets done (on time, on budget)',
        'Make final decisions when needed',
        'Remove blockers for Responsible parties',
        'Accept or reject deliverables',
        'Take ownership of success or failure',
      ],
      example:
        'For "Launch new product feature," Accountable = VP Product (owns outcome, even though doesn\'t do all work)',
      questions: ['Who owns the outcome?', 'Who has final say?', 'Who approves "done"?'],
      tip: 'Choose one accountable owner so decisions have a clear home. That person can still work closely with everyone contributing to the result.',
    },
    {
      letter: 'C',
      title: 'Consulted',
      color: 'green',
      subtitle: 'Provides Input',
      description:
        "People whose opinions matter. They're consulted before decisions. Two-way communication: they give input, you respond. Subject matter experts, stakeholders.",
      responsibilities: [
        'Provide expert input when asked',
        'Review proposals and give feedback',
        'Share relevant information',
        'Raise concerns or risks',
        'Respond in reasonable timeframe',
      ],
      example:
        'For "Launch new product feature," Consulted = Customer Success (knows customer pain), Legal (compliance), Sales (go-to-market)',
      questions: [
        'Whose opinion do we need?',
        'Who has critical expertise?',
        'Who will be impacted?',
      ],
      tip: "Limit Consulted. If you consult 20 people, you'll never launch. Keep to 3-5 critical voices. Others can be Informed instead.",
    },
    {
      letter: 'I',
      title: 'Informed',
      color: 'orange',
      subtitle: 'Kept Updated',
      description:
        "People who need to know what's happening, but don't provide input. One-way communication: you tell them. No approval needed.",
      responsibilities: [
        'Stay informed of progress and decisions',
        'Read updates (not ignore)',
        'Ask questions if confused',
        'Prepare their teams/areas',
        'No approval or input required',
      ],
      example:
        'For "Launch new product feature," Informed = Support team (needs to know), Finance (for planning), Rest of company (all-hands)',
      questions: [
        'Who needs to know?',
        'Who will be affected?',
        'Who needs to prepare their teams?',
      ],
      tip: 'Keep the informed group updated on the decisions and progress that affect them. Be clear about when input is needed and when an update is enough.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Execution & Monitoring', path: '/execution-monitoring' },
              { label: 'Accountability & Ownership' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <TrendingUp className="w-4 h-4 mr-2" />
            Execution & Monitoring Tool 4 of 4
          </span>
        }
        title="Give every priority a clear owner"
        subtitle="Remove uncertainty about who does the work, makes the decision, and needs to be involved. Use RACI to connect your strategic priorities to clear responsibilities."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Clarify ownership
        </Link>
      </PageHero>

      {/* RACI Matrix Deep Dive */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">
              Make responsibility visible before work starts
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When everyone is involved, ownership can get blurred. RACI helps you distinguish the
              person doing the work from the person accountable for the result.
            </p>
          </motion.div>
          <div className="max-w-6xl mx-auto space-y-8">
            {raciRoles.map((role, idx) => (
              <motion.div
                key={idx}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`public-card public-card--white p-8 rounded-xl`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`${themeClasses[role.color].badge} text-white w-16 h-16 rounded-full flex items-center justify-center text-3xl font-bold`}
                    >
                      {role.letter}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{role.title}</h3>
                      <p className={`text-sm ${themeClasses[role.color].text} font-medium`}>
                        {role.subtitle}
                      </p>
                    </div>
                  </div>
                  <div className={`${themeClasses[role.color].soft} px-4 py-2 rounded-lg`}>
                    <p
                      className={`text-xs ${themeClasses[role.color].text} font-semibold uppercase`}
                    >
                      RACI Role {idx + 1} of 4
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mb-6 text-lg">{role.description}</p>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className={`${themeClasses[role.color].tint} p-4 rounded-lg`}>
                    <p className="text-sm font-semibold text-gray-900 mb-3">
                      Key Responsibilities:
                    </p>
                    <ul className="space-y-2">
                      {role.responsibilities.map((resp, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <CheckCircle
                            className={`w-3 h-3 ${themeClasses[role.color].icon} mr-2 mt-0.5 flex-shrink-0`}
                          />
                          {resp}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900 mb-3">Critical Questions:</p>
                    <ul className="space-y-2 mb-4">
                      {role.questions.map((q, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <Target className="w-3 h-3 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          {q}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-white p-3 rounded border-l-4 border-green-500">
                      <p className="text-xs font-semibold text-gray-900 mb-1">Example:</p>
                      <p className="text-xs text-gray-700">{role.example}</p>
                    </div>
                  </div>
                </div>
                <div className={`${themeClasses[role.color].tip} p-4 rounded-lg border-l-4`}>
                  <p className="text-sm text-gray-700">
                    <span className={`font-semibold ${themeClasses[role.color].text}`}>
                      Pro Tip:
                    </span>{' '}
                    {role.tip}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Create RACI */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Create an Effective RACI Matrix</h2>
              <p className="text-xl text-gray-600">Six steps to assign clear accountability</p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'List All Key Decisions and Deliverables',
                  description:
                    "Start with inventory. For initiative/project: What are the major decisions? What are the deliverables? Each needs RACI. Don't RACI tiny tasks—focus on meaningful decisions and outputs.",
                  tip: 'Use "decision log" and "deliverable list" as inputs. Typical project has 10-20 key decisions/deliverables worth RACI-ing. More = too detailed, fewer = gaps.',
                },
                {
                  step: '2',
                  title: 'Identify Stakeholders',
                  description:
                    'Who are the players? List all people who might be involved: leaders, team members, SMEs, cross-functional partners. Names, not roles. Real people.',
                  tip: 'Include: Decision makers, Subject matter experts, People who do work, People affected by outcome. Err on inclusive—better to assign "I" than miss someone.',
                },
                {
                  step: '3',
                  title: 'Assign ONE Accountable per Item',
                  description:
                    'For each decision/deliverable, pick ONE person who owns outcome. Not two. Not "team." One name. This is person who approves done, makes final call, owns result.',
                  tip: 'Test: "If this fails, who do we fire?" That\'s your A. If answer is "well, it depends" or "both X and Y," you haven\'t assigned clear A.',
                },
                {
                  step: '4',
                  title: 'Assign Responsible (Doers)',
                  description:
                    'Who actually does the work? Can be multiple people. But keep it to those truly executing, not peripherally involved. R does work, A owns outcome.',
                  tip: "Rule: If someone spends <10% of their time on this, they're probably not R—they're C or I. R means significant time commitment.",
                },
                {
                  step: '5',
                  title: 'Limit Consulted (Inputs)',
                  description:
                    "Who must we consult before deciding? Keep small. Every C adds delay. If you consult 15 people, you'll never move. Prioritize ruthlessly: Who has critical, non-substitutable input?",
                  tip: 'Ask: "Could we make reasonable decision without this person\'s input?" If yes, make them I, not C. Consultation should be exception, not rule.',
                },
                {
                  step: '6',
                  title: 'Document and Communicate',
                  description:
                    'Create matrix: rows = decisions/deliverables, columns = people, cells = R/A/C/I. Share with everyone. Get agreement. When conflict arises, refer to RACI. Update as needed.',
                  tip: 'Keep your RACI matrix in a shared document. Connect the resulting ownership decisions to people, projects and tasks in StratPlan, and review them when scope or team roles change.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Accountability Beyond RACI */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Building Accountability Culture Beyond RACI
              </h2>
              <p className="text-xl text-gray-600">Four pillars of organizational accountability</p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  pillar: 'Transparent Scorecards',
                  icon: BarChart3,
                  description:
                    "Every key owner has visible scorecard: OKRs, KPIs, initiatives. Everyone sees everyone else's progress. Transparency = accountability.",
                  practices: [
                    'Public dashboards for all OKR owners',
                    'Weekly status updates (red/yellow/green)',
                    'Performance visible to peers and leaders',
                    'No hiding behind "we\'re working on it"',
                  ],
                  benefit:
                    'When your performance is public, you perform. Peer pressure > boss pressure. Transparency drives results.',
                  company:
                    'Netflix: "Sunlight is best disinfectant." All OKRs and metrics visible company-wide. Creates accountability through visibility.',
                },
                {
                  pillar: 'Consequences (Positive & Negative)',
                  icon: Award,
                  description:
                    'Accountability requires consequences. Deliver = rewards/recognition. Miss = tough conversations/changes. No consequences = no accountability.',
                  practices: [
                    'Bonuses tied to OKR/KPI achievement',
                    'Public recognition for delivering results',
                    'Performance improvement plans for chronic misses',
                    'Promotion based on delivery track record',
                  ],
                  benefit:
                    "Humans respond to incentives. If there's no upside for delivering and no downside for missing, why bust ass?",
                  company:
                    "Amazon: Leaders who consistently miss commitments don't last. Clear consequences. But also massive rewards for delivering.",
                },
                {
                  pillar: 'Commitment Culture',
                  icon: Shield,
                  description:
                    'When you commit, you deliver. Period. Culture where commitments are sacred, not aspirational. Missing commitment = big deal.',
                  practices: [
                    "Leaders model: Only commit what you'll deliver",
                    'Under-promise, over-deliver',
                    "If you'll miss, flag early—don't surprise",
                    'Team holds each other accountable',
                  ],
                  benefit:
                    "In strong culture, peer accountability > manager accountability. Team doesn't tolerate non-delivery.",
                  company:
                    'Navy SEALs: "Under pressure, you don\'t rise to occasion—you fall to level of training." Culture of commitment in DNA.',
                },
                {
                  pillar: 'Clear Decision Rights',
                  icon: Users,
                  description:
                    "Beyond who owns outcome, clarify who decides what. Decision ambiguity = accountability ambiguity. Map: What decisions? Who decides? What's the process?",
                  practices: [
                    'Decision authority matrix (DACI or similar)',
                    'Escalation paths defined',
                    'No "we\'ll discuss more"—clear decision maker',
                    'Document decisions and rationale',
                  ],
                  benefit:
                    'Clarity enables speed. When everyone knows who decides, decisions happen fast. Ambiguity = endless meetings.',
                  company:
                    'Apple: Clear decision makers. No committees. Person accountable has authority to decide. Speed through clarity.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-8 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-purple-600 p-3 rounded-lg">
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold">{item.pillar}</h3>
                  </div>
                  <p className="text-gray-700 mb-4">{item.description}</p>
                  <div className="bg-purple-100 p-4 rounded-lg mb-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Key Practices:</p>
                    <ul className="space-y-1">
                      {item.practices.map((practice, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <CheckCircle className="w-3 h-3 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                          {practice}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg mb-3">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">Benefit:</span> {item.benefit}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-700">
                      <span className="font-semibold text-blue-700">Example:</span> {item.company}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Accountability Mistakes</h2>
              <p className="text-xl text-gray-600">
                Pitfalls that undermine ownership and execution
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Multiple "Accountable" People',
                  description:
                    'Assigning 2+ people as Accountable for same outcome. Result: both think other is handling it. Nothing happens. Or they conflict over decisions.',
                  fix: 'Iron rule: ONE Accountable per outcome. Never violate. If you think you need two, split into two separate outcomes with one A each.',
                },
                {
                  mistake: 'Team as Owner',
                  description:
                    'Making "the product team" or "leadership team" accountable. Teams aren\'t accountable—people are. "Team accountability" = no accountability.',
                  fix: 'Always name a person. "Sarah owns this" not "product team owns this." One human, one email address, one performance review.',
                },
                {
                  mistake: 'Too Many Consulted',
                  description:
                    'Consulting 15 people before every decision. Result: paralysis. Consultations become negotiations. Nothing moves. Death by consensus.',
                  fix: "Limit C to 3-5 max per decision. If more, you're doing it wrong. Accountable person decides after consulting—doesn't need unanimous agreement.",
                },
                {
                  mistake: 'RACI Without Follow-Through',
                  description:
                    'Creating beautiful RACI matrix, sharing it, then ignoring it. When conflict arises, not referring to RACI. It becomes shelf-ware.',
                  fix: 'Use RACI actively. When someone says "who owns this?", point to RACI. When conflict, RACI arbitrates. Make it living, referred-to document.',
                },
                {
                  mistake: 'No Consequences for Missing',
                  description:
                    "Person misses commitment. No discussion. No change. Try again next quarter. Result: people learn commitments don't matter.",
                  fix: "Address misses immediately. Root cause: What happened? What'll we change? If chronic pattern, performance issue. Accountability requires consequences.",
                },
                {
                  mistake: 'Confusing "Involved" with "Accountable"',
                  description:
                    "Everyone who attends meetings about topic thinks they're accountable. Result: diffusion of responsibility. Real owner gets drowned out.",
                  fix: 'Clear communication: "Sarah is Accountable—she decides. Rest of us are C or I." Explicitly clarify roles, especially at meeting start.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Create clarity around the people behind your plan
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Name the deliverable</h3>
                <p className="text-gray-600">
                  Start with a specific decision, initiative, or outcome that needs ownership.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Assign the roles</h3>
                <p className="text-gray-600">
                  Identify who is responsible, accountable, consulted, and informed for that work.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Confirm the commitment</h3>
                <p className="text-gray-600">
                  Agree on expectations and review points so each owner knows what happens next.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/execution-monitoring/okrs-kpis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">OKRs & KPIs</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Assign clear owners for every OKR and KPI
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/strategic-initiatives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Zap className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Strategic Initiatives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Single owner required for every initiative
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/strategic-cadence"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <TrendingUp className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Strategic Cadence</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Review accountability at each cadence level
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/execution-monitoring/continuous-improvement"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Continuous Improvement
              </Link>
              <Link
                to="/sign-up"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Get Started with StratPlan <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">Clarify ownership in StratPlan</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Agree on expectations and review points so each owner knows what happens next. Start
            with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Clarify ownership <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
